import { useCallback, useMemo } from "react";
import { debug as debugFn } from "debug";
import { errAsync, ResultAsync } from "neverthrow";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";

import { dayjs } from "@thalamos/common";
import type {
  ExtendedVisionUser,
  IncidentWorkItemWithContext,
  WorkflowVersion,
} from "@thalamos/vision-common";
import {
  calculateEndIncidentActions,
  endIncidentDestination,
  FormSectionSchema,
  IncidentFormTypeSchema,
  IncidentWorkItemIdSchema,
  workflows,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import { useUser } from "../../Context/UserContext.js";
import type { McaCapacityAssessmentFormProps } from "../../forms/McaCapacityAssessment/McaCapacityAssessmentForm.js";
import {
  McaCapacityAssessmentForm,
  McaCapacityAssessmentFormSkeleton,
} from "../../forms/McaCapacityAssessment/McaCapacityAssessmentForm.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { createRoute } from "../../routing/createRoute.js";
import { endIncidentRoute } from "../../routing/endIncidentRoute.js";
import { Error as ErrorComponent } from "../Error/Error.js";

const debug = debugFn("vision-frontend:McaCapacityAssessmentPage");

export type McaCapacityAssessmentPageProps = Record<string, unknown>;

// This component is responsible for managing the API calls and passing
// the data to the inner component.
export function McaCapacityAssessmentPage(
  _props: McaCapacityAssessmentPageProps,
) {
  const params = useParams();

  const incidentWorkItemId = IncidentWorkItemIdSchema.safeParse(params.id);
  if (!incidentWorkItemId.success) {
    throw new Error("Invalid incident work item id");
  }

  const [
    { data: incidentWorkItem, error: incidentWorkItemError },
    refetchWorkItem,
  ] = apiHooks.incidentWorkItem.get(incidentWorkItemId.data);

  const { user } = useUser();

  if (incidentWorkItemError || user === undefined) {
    return <ErrorComponent />;
  }

  return (
    <McaCapacityAssessmentPageInner
      incidentWorkItem={incidentWorkItem ?? undefined}
      refetchWorkItem={refetchWorkItem}
      user={user}
    />
  );
}

export type McaCapacityAssessmentPageInnerProps = {
  incidentWorkItem?: IncidentWorkItemWithContext;
  refetchWorkItem: ReturnType<typeof apiHooks.incidentWorkItem.get>[1];
  user?: ExtendedVisionUser;
};

export function McaCapacityAssessmentPageInner({
  incidentWorkItem,
  refetchWorkItem,
  user,
}: McaCapacityAssessmentPageInnerProps) {
  if (incidentWorkItem === undefined || user === undefined) {
    return <McaCapacityAssessmentPageInnerLoading />;
  }

  const hasMcaForm = incidentWorkItem.forms.some(
    (form) => form.formType === IncidentFormTypeSchema.enum.mentalCapacityAct,
  );
  if (!hasMcaForm) {
    return (
      <Navigate
        replace
        to={createRoute.incidentProgressPage(
          incidentWorkItem.incidentWorkItem.id,
        )}
      />
    );
  }

  return (
    <McaCapacityAssessmentPageInnerLoaded
      incidentWorkItem={incidentWorkItem}
      refetchWorkItem={refetchWorkItem}
      user={user}
    />
  );
}

function McaCapacityAssessmentPageInnerLoading() {
  return <McaCapacityAssessmentFormSkeleton />;
}

export function McaCapacityAssessmentPageInnerLoaded({
  incidentWorkItem,
  refetchWorkItem,
  user,
}: Required<McaCapacityAssessmentPageInnerProps>) {
  const navigate = useNavigate();
  const toastNotifications = useToastNotifications();
  const { t } = useTranslation();

  const incidentWorkItemId = incidentWorkItem.incidentWorkItem.id;
  const workflowVersion = incidentWorkItem.incidentWorkItem
    .workflowVersion as WorkflowVersion;

  const mcaForm = useMemo(
    () =>
      incidentWorkItem.forms.find(
        (form) =>
          form.formType === IncidentFormTypeSchema.enum.mentalCapacityAct,
      ),
    [incidentWorkItem.forms],
  );
  if (!mcaForm) {
    throw new Error("Mental Capacity Act form not found");
  }

  const mcaFormId = mcaForm.id;

  // Get the form specification (including pre-computed defaultValues)
  const formSpecification = useMemo(() => {
    const existingData =
      mcaForm.sections.find(
        (section) =>
          section.id === FormSectionSchema.enum.mcaCapacityAssessment,
      )?.data ?? {};
    return workflows[workflowVersion]?.[
      "mentalCapacityAct"
    ].formDefinition.sections
      .find(
        (section) =>
          section.id === FormSectionSchema.enum.mcaCapacityAssessment,
      )
      ?.buildFormSpecification({ incidentWorkItem, existingData });
  }, [workflowVersion, incidentWorkItem, mcaForm.sections]);

  if (!formSpecification) {
    throw new Error("Form specification not found");
  }

  const { formSchema: schema, defaultValues: initialValues } =
    formSpecification;

  const existingValuesWereValid = useMemo(() => {
    const existingData =
      mcaForm.sections.find(
        (section) =>
          section.id === FormSectionSchema.enum.mcaCapacityAssessment,
      )?.data ?? {};
    return schema.safeParse(existingData).success;
  }, [schema, mcaForm.sections]);

  const enableAutoSave = useMemo(
    () =>
      workflows[workflowVersion]?.[
        "mentalCapacityAct"
      ].formDefinition.sections.find(
        (section) =>
          section.id === FormSectionSchema.enum.mcaCapacityAssessment,
      )?.autoSave ?? false,
    [workflowVersion],
  );

  const onAutoSave: Exclude<
    McaCapacityAssessmentFormProps<typeof workflowVersion>["onAutoSave"],
    undefined
  > = useCallback(
    (values) => {
      if (existingValuesWereValid) {
        // Because we started off with a valid and complete form, we should not regress
        // to a partial form.
        const isValid = schema.safeParse(values).success;
        if (!isValid) {
          return errAsync("Blocked auto-save: form is not valid");
        }
      }
      return ResultAsync.fromPromise(
        api.incidentWorkItem.updateData(
          incidentWorkItemId,
          mcaFormId,
          FormSectionSchema.enum.mcaCapacityAssessment,
          values,
          existingValuesWereValid,
        ),
        (error) =>
          error instanceof Error ? error.message : JSON.stringify(error),
      )
        .orTee(debug)
        .map(() => true);
    },
    [incidentWorkItemId, existingValuesWereValid, schema, mcaFormId],
  );

  const onSubmit: McaCapacityAssessmentFormProps<
    typeof workflowVersion
  >["onSubmit"] = useCallback(
    async (values: Record<string, unknown>) => {
      try {
        const response = await api.incidentWorkItem.updateData(
          incidentWorkItemId,
          mcaFormId,
          FormSectionSchema.enum.mcaCapacityAssessment,
          values,
          true,
        );
        if (response.status === 204) {
          await refetchWorkItem();

          // Check if user chose to end the form at any early-exit point
          const earlyExitSchema = z.object({
            value: z.enum(["end", "continue"]).optional(),
          });
          const userChoseToEndForm = (
            [
              "earlyExitCapable",
              "earlyExitNoImpairment",
              "earlyExitNoLackOfCapacity",
            ] as const
          ).some(
            (fieldName) =>
              earlyExitSchema.safeParse(values[fieldName]).data?.value ===
              "end",
          );
          if (userChoseToEndForm) {
            navigate(
              endIncidentRoute(
                incidentWorkItemId,
                endIncidentDestination(
                  calculateEndIncidentActions({
                    incidentWorkItem,
                    user,
                    asOfDateTime: dayjs(),
                  }),
                ),
              ),
            );
            return;
          }

          navigate(`/incident/${incidentWorkItemId}/section-complete`, {
            state: { type: FormSectionSchema.enum.mcaCapacityAssessment },
          });
          return;
        } else {
          throw new Error(JSON.stringify(response.data.message));
        }
      } catch (error) {
        debug("Failed to update MCA Capacity Assessment section", error);
        toastNotifications.show(
          t("incidentWorkItem.notificationFailedToSave"),
          {
            severity: "error",
          },
        );
      }
    },
    [
      incidentWorkItemId,
      incidentWorkItem,
      user,
      navigate,
      refetchWorkItem,
      mcaFormId,
      t,
      toastNotifications,
    ],
  );

  const onCancel: McaCapacityAssessmentFormProps<
    typeof workflowVersion
  >["onCancel"] = useCallback(() => {
    navigate(createRoute.incidentProgressPage(incidentWorkItemId));
  }, [incidentWorkItemId, navigate]);

  return (
    <McaCapacityAssessmentForm
      onAutoSave={enableAutoSave ? onAutoSave : undefined}
      onSubmit={onSubmit}
      onCancel={onCancel}
      initialValues={initialValues}
      incidentWorkItem={incidentWorkItem}
      workflowVersion={workflowVersion}
    />
  );
}
